import { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL, formatApiError } from '../utils/api';
import { AuthShell } from './ForgotPasswordPage';
import { Eye, EyeSlash, ArrowRight, ArrowLeft, CheckCircle, WarningCircle, LockKey } from '@phosphor-icons/react';

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') || '';

  const [checking, setChecking] = useState(true);
  const [valid, setValid] = useState(false);
  const [hint, setHint] = useState('');
  const [pwd, setPwd] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) { setChecking(false); setValid(false); return; }
    axios.get(`${API_URL}/api/auth/reset-password/validate`, { params: { token } })
      .then((r) => { setValid(Boolean(r.data?.valid)); setHint(r.data?.email_hint || ''); })
      .catch(() => setValid(false))
      .finally(() => setChecking(false));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (pwd.length < 8) { setError('Password must be at least 8 characters'); return; }
    if (pwd !== confirm) { setError('Passwords do not match'); return; }
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/reset-password`, { token, new_password: pwd });
      setDone(true);
      setTimeout(() => navigate('/login', { replace: true }), 2500);
    } catch (err) {
      setError(formatApiError(err, 'Could not reset password. Please request a new link.'));
    } finally {
      setLoading(false);
    }
  };

  if (checking) {
    return (
      <AuthShell testid="reset-password-page">
        <div className="text-center py-6 text-sm text-gray-400" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Verifying link…</div>
      </AuthShell>
    );
  }

  if (done) {
    return (
      <AuthShell testid="reset-password-page">
        <div className="text-center" data-testid="reset-success">
          <div className="mx-auto mb-4 w-14 h-14 rounded-full bg-[#DCEBE2] flex items-center justify-center">
            <CheckCircle size={30} weight="fill" className="text-[#2F6D4B]" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 tracking-tight" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>Password reset</h2>
          <p className="text-sm text-gray-500 mt-2">Your password has been updated. Redirecting you to sign in…</p>
          <Link to="/login" className="inline-flex items-center gap-2 mt-6 text-sm font-semibold text-[#0A2540] hover:underline" data-testid="back-to-login">
            <ArrowLeft size={15} /> Go to sign in
          </Link>
        </div>
      </AuthShell>
    );
  }

  if (!valid) {
    return (
      <AuthShell testid="reset-password-page">
        <div className="text-center" data-testid="reset-invalid">
          <div className="mx-auto mb-4 w-14 h-14 rounded-full bg-[#F6DDD9] flex items-center justify-center">
            <WarningCircle size={30} weight="fill" className="text-[#B4453A]" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 tracking-tight" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>Link expired or invalid</h2>
          <p className="text-sm text-gray-500 mt-2">This recovery link is no longer valid. Recovery links expire after 15 minutes and can only be used once.</p>
          <Link to="/forgot-password" className="inline-flex items-center gap-2 mt-6 text-sm font-semibold text-[#0A2540] hover:underline" data-testid="request-new-link">
            Request a new link <ArrowRight size={15} />
          </Link>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell testid="reset-password-page">
      <div className="text-center mb-7">
        <h2 className="text-2xl font-bold text-gray-900 tracking-tight" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>Set a new password</h2>
        <p className="text-sm text-gray-400 mt-1">{hint ? <>For <span className="font-semibold text-gray-500">{hint}</span></> : 'Choose a strong new password.'}</p>
      </div>
      {error && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-600 px-3 py-2.5 rounded-lg mb-5" data-testid="reset-error">
          <WarningCircle size={16} /><span className="text-xs">{error}</span>
        </div>
      )}
      <form onSubmit={submit} className="space-y-5">
        <PwdField label="New password" value={pwd} onChange={setPwd} show={show} setShow={setShow} testid="reset-new-password" />
        <PwdField label="Confirm new password" value={confirm} onChange={setConfirm} show={show} setShow={setShow} testid="reset-confirm-password" />
        <p className="text-[11px] text-gray-400 -mt-2">At least 8 characters. Can't reuse your last 3 passwords.</p>
        <button type="submit" disabled={loading} data-testid="reset-submit-btn"
          className="w-full h-11 rounded-md text-white font-semibold text-sm transition-all duration-200 hover:shadow-lg hover:shadow-[#0A2540]/20 disabled:opacity-50 flex items-center justify-center gap-2"
          style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', background: 'linear-gradient(135deg, #0A2540 0%, #0991d6 100%)' }}>
          {loading ? (<><span className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Saving...</>) : (<>Reset password<ArrowRight size={16} /></>)}
        </button>
      </form>
      <div className="mt-6 pt-4 border-t border-gray-100 text-center">
        <Link to="/login" className="inline-flex items-center gap-2 text-sm font-semibold text-[#0A2540] hover:underline" data-testid="back-to-login">
          <ArrowLeft size={15} /> Back to sign in
        </Link>
      </div>
    </AuthShell>
  );
}

function PwdField({ label, value, onChange, show, setShow, testid }) {
  return (
    <div className="space-y-1.5">
      <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{label}</label>
      <div className="relative">
        <LockKey size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type={show ? 'text' : 'password'} value={value} onChange={(e) => onChange(e.target.value)}
          placeholder="••••••••" required data-testid={testid}
          className="w-full h-11 pl-10 pr-10 rounded-md border border-gray-200 bg-transparent text-gray-900 text-sm shadow-sm placeholder:text-gray-300 focus:outline-none focus:border-[#0A2540] focus:ring-1 focus:ring-[#0A2540]/20 transition-colors"
          style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
        />
        <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
          {show ? <EyeSlash size={16} /> : <Eye size={16} />}
        </button>
      </div>
    </div>
  );
}
